import React from "react";
import { AiCapabilityResultRow } from "../ai-capability.types";
import { AiOmnibarEntry, AiOmnibarTurn, useAiOmnibar } from "../useAiOmnibar";

/**
 * The AI half of the global search bar. It lives inside the Angular `global-search` template
 * so the two modes share one pill; everything visual is in `admin/styles/custom.css` under
 * `.navbar-search`, which is what lets the search input and this one cross-fade together.
 */
type Props = {
  isActive: boolean;
  onExit: () => void;
};

/** Must match the omni-turn-out animation in custom.css. */
const TURN_EXIT_MS = 260;

const AiOmnibar = (props: Props) => {
  const { entries, turns, isRunning, submit, clear, runResultRow } = useAiOmnibar();
  const [text, setText] = React.useState("");
  const [isFocused, setIsFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const listRef = React.useRef<HTMLDivElement>(null);
  const isOpenRef = React.useRef(false);
  const exitTimeout = React.useRef<number | null>(null);
  const [leavingTurn, setLeavingTurn] = React.useState<AiOmnibarTurn | null>(null);
  const currentTurn = turns.at(-1) ?? null;
  const previousTurnRef = React.useRef<AiOmnibarTurn | null>(null);

  React.useEffect(() => {
    if (props.isActive) {
      inputRef.current?.focus();
    }
  }, [props.isActive]);

  React.useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [entries]);

  // When a new query starts, the one before it slides up and out rather than just vanishing.
  React.useEffect(() => {
    const previous = previousTurnRef.current;
    previousTurnRef.current = currentTurn;

    if (previous === null || currentTurn === null || previous.id === currentTurn.id) {
      return;
    }

    setLeavingTurn(previous);

    if (exitTimeout.current !== null) {
      window.clearTimeout(exitTimeout.current);
    }

    exitTimeout.current = window.setTimeout(() => setLeavingTurn(null), TURN_EXIT_MS);
  }, [currentTurn]);

  React.useEffect(() => {
    return () => {
      if (exitTimeout.current !== null) {
        window.clearTimeout(exitTimeout.current);
      }
    };
  }, []);

  const handleFocus = () => setIsFocused(true);

  /**
   * Losing focus is not the same as being done with the panel.
   *
   * It used to close on blur, which meant the answer vanished the moment you clicked into it - so
   * text could be read but never selected or copied. Now the panel stays until you click somewhere
   * else or press Escape, and a click inside it does nothing but let you select.
   */
  React.useEffect(() => {
    if (!isOpenRef.current) {
      return;
    }

    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;

      if (target instanceof Node && rootRef.current?.contains(target) === true) {
        return;
      }

      setIsFocused(false);
    };

    document.addEventListener("mousedown", onPointerDown);

    return () => document.removeEventListener("mousedown", onPointerDown);
  });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "Enter": {
        const submitted = text;
        setText("");
        void submit(submitted);
        break;
      }

      case "Escape": {
        if (entries.length > 0) {
          clear();
        } else {
          props.onExit();
        }
        break;
      }

      default:
        break;
    }
  };

  const isOpen = props.isActive && isFocused;
  const hasConversation = entries.length > 0 || isRunning;

  isOpenRef.current = isOpen;

  return (
    <div ref={rootRef} className="ai-omnibar">
      <input
        ref={inputRef}
        aria-label="Ask AI"
        className="ai-omnibar-input"
        placeholder="Ask AI to open or start something..."
        tabIndex={props.isActive ? 0 : -1}
        value={text}
        onChange={(event) => setText(event.target.value)}
        onFocus={handleFocus}
        onKeyDown={handleKeyDown}
      />

      {isRunning ? <span className="ai-omnibar-spinner" /> : null}

      {isOpen ? (
        <div className="ai-omnibar-dropdown">
          {/*
            Shown whenever the dropdown is, not once and dismissible: the request goes to a model
            outside this system, and the person typing is usually mid-task with a chart open.
          */}
          <div className="ai-omnibar-notice" role="note">
            This message goes to an AI. Do not enter PHI.
          </div>

          {hasConversation ? (
            <div className="ai-omnibar-controls">
              {/*
                Escape does this too, but nothing on screen said so. A step that failed leaves the
                panel holding a dead end, and the way out should not be a keyboard shortcut nobody
                was told about.
              */}
              <button
                className="ai-omnibar-clear"
                type="button"
                onClick={() => clear()}
                onMouseDown={(event) => event.preventDefault()}
              >
                Start over
              </button>
            </div>
          ) : null}

          {hasConversation ? (
            <div ref={listRef} className="ai-omnibar-panel">
              {leavingTurn === null ? null : (
                <div key={leavingTurn.id} className="ai-omnibar-turn is-leaving">
                  {leavingTurn.entries.map((entry) => (
                    <AiOmnibarEntryRow key={entry.id} entry={entry} onRunResult={() => undefined} />
                  ))}
                </div>
              )}
              {currentTurn === null ? null : (
                <div key={currentTurn.id} className="ai-omnibar-turn">
                  {currentTurn.entries.map((entry) => (
                    <AiOmnibarEntryRow
                      key={entry.id}
                      entry={entry}
                      onRunResult={(row) => void runResultRow(row)}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const AiOmnibarEntryRow = (props: {
  entry: AiOmnibarEntry;
  onRunResult: (row: AiCapabilityResultRow) => void;
}) => {
  const { entry } = props;

  switch (entry.kind) {
    case "user":
      return <div className="ai-omnibar-entry ai-omnibar-entry-user">{entry.text}</div>;

    case "assistant":
      return <div className="ai-omnibar-entry ai-omnibar-entry-assistant">{entry.text}</div>;

    case "note":
      return <div className="ai-omnibar-entry ai-omnibar-entry-note">{entry.text}</div>;

    case "step":
      return (
        <div className={`ai-omnibar-entry ai-omnibar-entry-step is-${entry.status}`}>
          <span className="ai-omnibar-step-marker" />
          <span>{entry.text}</span>
        </div>
      );

    case "results":
      return (
        <div className="ai-omnibar-results">
          {entry.rows.map((row) => (
            <button
              key={`${row.capabilityId}-${row.label}`}
              className="ai-omnibar-result"
              type="button"
              // mousedown rather than click, and default prevented, so the input keeps focus and
              // the panel does not close from under the pointer.
              onMouseDown={(event) => {
                event.preventDefault();
                props.onRunResult(row);
              }}
            >
              <span className="ai-omnibar-result-label">{row.label}</span>
              {row.detail === undefined ? null : (
                <span className="ai-omnibar-result-detail">{row.detail}</span>
              )}
            </button>
          ))}
        </div>
      );
  }
};

export default AiOmnibar;
